"use client";

import { cn } from "~/lib/utils";
import { ImageLoader } from "~/components/ImageLoader";
import { RWA_COIN_ICON } from "~/consts";
import { getCorpusAsset } from "../../assets";

// // // //

/**
 * Shared primitives for the GlobalChatDock, mirroring the "In-app AI chat"
 * design: a filled sparkle glyph, the gradient AI avatar tile, the monospace
 * ticker chip, and the compact relative-time / day-bucket helpers used by the
 * conversation list.
 */

/**
 * Filled four-point sparkle — the design's AI mark. lucide's `Sparkles` is
 * stroke-only, so this inline glyph matches the mock's solid star exactly and
 * reads crisply inside the gradient avatar tile.
 */
export function SparkleGlyph(props: { className?: string }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
            className={props.className}
        >
            <path d="M12 2l1.6 6.4L20 10l-6.4 1.6L12 18l-1.6-6.4L4 10l6.4-1.6z" />
        </svg>
    );
}

/** Gradient AI avatar tile (sparkle on a rounded blue chip). */
export function SparkleAvatar(props: {
    className?: string;
    glyphClassName?: string;
}) {
    return (
        <span
            aria-hidden="true"
            className={cn(
                "grid shrink-0 place-items-center rounded-xl bg-linear-to-br from-primary to-primary-dark text-primary-foreground shadow-lg shadow-primary/30 ring-1 ring-inset ring-white/15",
                props.className,
            )}
        >
            <SparkleGlyph className={cn("size-5", props.glyphClassName)} />
        </span>
    );
}

/** Small asset logo rendered beside a ticker eyebrow in list rows. */
export function AssetLogo(props: { assetID: string; className?: string }) {
    return (
        <ImageLoader
            src={getCorpusAsset(props.assetID)?.iconUrl ?? RWA_COIN_ICON}
            alt={`${assetLabel(props.assetID)} logo`}
            fallbackSrc={RWA_COIN_ICON}
            className={cn(
                "size-5 shrink-0 rounded-md object-cover",
                props.className,
            )}
        />
    );
}

/**
 * Monospace ticker chip. `active` (today's threads) gets the primary tint; the
 * rest get a neutral muted chip, echoing the design's two-tone list.
 */
export function TickerBadge(props: {
    ticker: string;
    active?: boolean;
    className?: string;
}) {
    return (
        <span
            className={cn(
                "inline-flex h-5 shrink-0 items-center rounded-md px-2 font-mono text-[10.5px] font-medium tracking-tight",
                props.active
                    ? "bg-primary/10 text-primary"
                    : "bg-muted text-tertiary-foreground",
                props.className,
            )}
        >
            {props.ticker}
        </span>
    );
}

// // // //

/** Human-readable label for a conversation row (corpus name, falling back to id). */
export function assetLabel(assetID: string): string {
    return getCorpusAsset(assetID)?.name ?? assetID;
}

/** Short blurb for a conversation row's second line (the asset's one-liner). */
export function assetSnippet(assetID: string): string | undefined {
    return getCorpusAsset(assetID)?.shortDescription;
}

/**
 * Compact relative time for list rows: "now", "2m", "1h", a weekday within the
 * last week ("Mon"), then a short date ("Jul 2"). Matches the design's terse
 * timestamps. Values arrive from tRPC as ISO strings, so coerce defensively.
 */
export function formatShortTime(value: Date | string): string {
    const then = new Date(value).getTime();
    if (Number.isNaN(then)) {
        return "";
    }

    const diffMinutes = Math.round((Date.now() - then) / 60_000);
    if (diffMinutes < 1) {
        return "now";
    }
    if (diffMinutes < 60) {
        return `${diffMinutes}m`;
    }

    const diffHours = Math.round(diffMinutes / 60);
    if (diffHours < 24) {
        return `${diffHours}h`;
    }

    const diffDays = Math.round(diffHours / 24);
    if (diffDays < 7) {
        return new Intl.DateTimeFormat("en", { weekday: "short" }).format(then);
    }

    return new Intl.DateTimeFormat("en", {
        month: "short",
        day: "numeric",
    }).format(then);
}

/** Whether a timestamp falls on the current calendar day (for Today/Earlier). */
export function isToday(value: Date | string): boolean {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return false;
    }
    const now = new Date();
    return (
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate()
    );
}

/** Turns a raw corpus PDF filename into a readable source-chip label. */
export function prettyDocTitle(title: string): string {
    return title
        .replace(/\.pdf$/i, "")
        .replace(/[-_]+/g, " ")
        .replace(/\s{2,}/g, " ")
        .trim();
}
